import React, { Suspense, lazy, useEffect, useRef } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { I18nextProvider } from "react-i18next";
import { Box, CircularProgress } from "@mui/material";
import i from "./locales/i18n";
import AppBar from "./pages/Main/AppBar";
import Menu from "./components/Menu/Menu";
import Footer from "./pages/Main/Footer";
import { useMyContext } from "./MyContext";
import { findPreferredLanguage } from "./assets/hooks/functions";
import ScrollButton from "./components/ScrollButton";
import Presentation from "./components/Presentation";
import HeroLayout from "./pages/Main/HeroLayout";
import About from "./pages/About";
import { useTranslation } from "react-i18next";
import rest from "./RESTDATA";
import Career from "./pages/Career";
import {
  getLanguageFromPathname,
  getLocalizedPath,
  normalizeLanguage,
  stripLanguageFromPathname,
} from "./locales/routing";

const Docs = lazy(() => import("./pages/Docs"));
const STORED_QUERY_PARAMS_KEY = "dushaQueryParams";
const QUERY_PARAMS_TTL_MS = 12 * 60 * 60 * 1000;
const STORED_QUERY_PARAM_NAMES = ["zont", "r", "dev", "menuOnly"];

function getStoredQueryParams() {
  try {
    const storedValue = window.sessionStorage.getItem(STORED_QUERY_PARAMS_KEY);
    if (!storedValue) return {};

    const storedParams = JSON.parse(storedValue);
    if (!storedParams.expiresAt || storedParams.expiresAt <= Date.now()) {
      window.sessionStorage.removeItem(STORED_QUERY_PARAMS_KEY);
      return {};
    }

    return storedParams.values || {};
  } catch (error) {
    return {};
  }
}

function saveStoredQueryParams(values) {
  try {
    window.sessionStorage.setItem(
      STORED_QUERY_PARAMS_KEY,
      JSON.stringify({
        values,
        expiresAt: Date.now() + QUERY_PARAMS_TTL_MS,
      })
    );
  } catch (error) {
    // Storage may be unavailable in private mode.
  }
}

function getCleanSearch(search) {
  const searchParams = new URLSearchParams(search);
  STORED_QUERY_PARAM_NAMES.forEach((paramName) => {
    searchParams.delete(paramName);
  });

  const cleanSearch = searchParams.toString();
  return cleanSearch ? `?${cleanSearch}` : "";
}

function PageLoader() {
  return (
    <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
      <CircularProgress color="inherit" />
    </Box>
  );
}

function App() {
  const { setLang, setZont, setRadius, setOnlyMenu,onlyMenuFromParams } = useMyContext();
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const menuRef = useRef();
  const headerRef = useRef();
  const pathnameWithoutLang = stripLanguageFromPathname(location.pathname);
  const urlLanguage = getLanguageFromPathname(location.pathname);

  const searchParams = new URLSearchParams(location.search);
  const paramsFromUrl = STORED_QUERY_PARAM_NAMES.reduce((params, paramName) => {
    const value = searchParams.get(paramName);
    if (value !== null) {
      params[paramName] = value;
    }
    return params;
  }, {});
  const hasStoredParamsInUrl = Object.keys(paramsFromUrl).length > 0;
  const storedParams = getStoredQueryParams();
  const queryParams = { ...storedParams, ...paramsFromUrl };
  const cleanSearch = getCleanSearch(location.search);

  let umbrella = queryParams.zont || null;

  const r = queryParams.r || rest.radiuses.restSpot;

  const dev = queryParams.dev || null;
  let menuOnly = queryParams.menuOnly || null;

  if (dev && !umbrella) {
    umbrella = "test";
  }

  if (umbrella || menuOnly==2) {
    menuOnly = "2";
  //  return ( <Box
  //   sx={{
  //     display: 'flex',
  //     justifyContent: 'center',
  //       alignItems: 'center',
  //     bgcolor: "primary.main",
  //     height: '100vh', // Full viewport height
  //     textAlign: 'center', // Optional: Center text alignment
  //     padding: 2, // Optional: Add some padding around the text
  //   }}
  // >
  //   <Typography variant="h5">
  //    Unfortunately, the domain is temporarily unavailable
  //   </Typography>
  // </Box>)
  }

  useEffect(() => {
    if (!hasStoredParamsInUrl) return;

    saveStoredQueryParams({ ...storedParams, ...paramsFromUrl });

    const cleanUrl = `${location.pathname}${cleanSearch}${location.hash}`;
    const currentUrl = `${location.pathname}${location.search}${location.hash}`;
    if (cleanUrl !== currentUrl) {
      navigate(cleanUrl, { replace: true });
    }
  }, [
    cleanSearch,
    hasStoredParamsInUrl,
    location.hash,
    location.pathname,
    location.search,
    navigate,
    paramsFromUrl,
    storedParams,
  ]);

  useEffect(() => {
    setZont(umbrella);
    setRadius(r);
    setOnlyMenu(menuOnly);
  }, [
    location.search,
    setZont,
    umbrella,
    r,
    setRadius,
    dev,
    menuOnly,
    setOnlyMenu,
  ]);

  useEffect(() => {
    if (urlLanguage) {
      setLang(urlLanguage);
      if (i18n.language !== urlLanguage) {
        i18n.changeLanguage(urlLanguage);
      }
      return;
    }

    const preferredLanguage = normalizeLanguage(
      findPreferredLanguage() || i18n.language
    );
    setLang(preferredLanguage);
    if (i18n.language !== preferredLanguage) {
      i18n.changeLanguage(preferredLanguage);
    }

    const localizedPath = `${getLocalizedPath(
      preferredLanguage,
      pathnameWithoutLang
    )}${cleanSearch}`;
    navigate(localizedPath, { replace: true });
  }, [
    cleanSearch,
    i18n,
    navigate,
    pathnameWithoutLang,
    setLang,
    urlLanguage,
  ]);

  const fallbackLanguage = normalizeLanguage(
    findPreferredLanguage() || i18n.language
  );
  const fallbackPath = `${getLocalizedPath(
    fallbackLanguage,
    pathnameWithoutLang
  )}${cleanSearch}`;
  

  return (
    <I18nextProvider i18n={ i }>

      <AppBar headerRef={headerRef} menuOnly={menuOnly} />
      { pathnameWithoutLang !== "/product-hero" &&
        pathnameWithoutLang !== "/docs" &&
        pathnameWithoutLang !== "/career" && (
          <HeroLayout scrollToMenu={menuOnly} devel={dev} />
        )}
      <Routes> 
        <Route
          path="/:lang"
          index
          element={<Menu headerRef={headerRef} menuRef={menuRef} />}
        /> 
        <Route
          path="/:lang/docs"
          element={(
            <Suspense fallback={<PageLoader />}>
              <Docs />
            </Suspense>
          )}
        /> 
        <Route path="/:lang/product-hero" element={<Presentation />} />
        <Route path="/:lang/career" element={<Career />} />
        <Route path="/:lang/about" element={<About />} />
        <Route path="*" element={<Navigate to={fallbackPath} replace />} />
      </Routes>
      <Footer />
      <ScrollButton />
    </I18nextProvider>
  );
}

export default App;
